/**
 * Responsibility: the market search and the collection, as query hooks.
 *
 * A search is not run as the operator types. It reads a product page per
 * result from somebody else's free site, so it fires when a query is
 * submitted and not before - `enabled` is what enforces that, and the query
 * key holds the submitted terms rather than the box's contents.
 *
 * The collection is the opposite: small, local, and invalidated by every
 * mutation, so it refetches eagerly.
 */
import {
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useDurableCollection } from './useDurableCollection';
import { useDurableSaved } from './useDurableSaved';
import { hubApi } from '../client';
import type {
  AddHolding,
  EditHolding,
  LikedBoard,
  MarketQuery,
  SaveSearchBody,
} from '../client';

/** Searches are expensive and their answers do not change minute to minute. */
const SEARCH_STALE_MS = 5 * 60 * 1000;

/**
 * Deliberately not under `['saved']`: liking from anywhere invalidates that
 * key, and a prefix match would make every heart-click re-read a catalogue
 * page for every card in the list.
 */
/**
 * The priced likes board.
 *
 * **Under `saved`, and that prefix is load-bearing.** Every mutation that
 * changes a like invalidates `['saved']`, which refreshes the raw list but
 * used to leave this board alone - it was keyed `['liked-cards']`, which
 * shares no prefix with anything. So liking a card updated the heart on the
 * row it was clicked from and the Likes tab kept showing the old set until
 * the page was reloaded by hand, which is exactly how it was reported.
 *
 * Keeping it in the same family means a new list cannot be added later and
 * quietly miss the same invalidation.
 */
const LIKED_CARDS_KEY = ['saved', 'likes', 'cards'] as const;

export const marketKeys = {
  search: (params: MarketQuery) => ['market', 'search', params] as const,
  card: (key: string) => ['market', 'card', key] as const,
  collection: () => ['collection'] as const,
};

/**
 * Results for a submitted query.
 *
 * Pass null until there is something to search for. A hook that fired on
 * every keystroke would hammer a free catalogue and rank nothing useful.
 */
export function useMarketSearch(params: MarketQuery | null) {
  /**
   * Paged, and accumulating.
   *
   * The catalogue finds far more cards than one window shows, and asking
   * somebody to press a button for each of the next twenty-four is asking
   * them to do the scrolling twice. Pages are appended as they arrive and
   * the table simply grows to whatever the search reported.
   *
   * The offset walks the same candidate list, so a later page is not
   * another search - it is the rest of the answer to the one already run.
   */
  return useInfiniteQuery({
    queryKey: marketKeys.search(params ?? { q: '', mode: 'TOP_VALUE' }),
    queryFn: ({ pageParam }) =>
      hubApi.marketSearch({ ...(params as MarketQuery), offset: pageParam }),
    initialPageParam: 0,
    getNextPageParam: (last, pages) => {
      const loaded = pages.reduce((count, page) => count + page.results.length, 0);
      // `candidates` is everything the index matched; `results` is what this
      // window carried. Stop when the two meet, and stop on an empty page
      // however the arithmetic looks - a page that returned nothing cannot
      // be followed by one that returns something.
      if (last.results.length === 0 || loaded >= last.candidates) return undefined;
      return loaded;
    },
    enabled: params !== null && params.q.trim().length > 1,
    staleTime: SEARCH_STALE_MS,
  });
}

/** One card and the sales behind its prices. Loads when a dialog opens. */
export function useMarketCard(key: string | null) {
  return useQuery({
    queryKey: marketKeys.card(key ?? ''),
    queryFn: () => hubApi.marketCard(key as string),
    enabled: key !== null,
    staleTime: SEARCH_STALE_MS,
  });
}

/**
 * Scan a phrase for listings under their own grade's market.
 *
 * Pass null until a scan is asked for. This is the most expensive call in
 * the app - a catalogue page and two eBay searches per card - so it never
 * fires on a page load, and the answer is kept for as long as a search is.
 */
export function useOutlierScan(query: string | null) {
  return useQuery({
    queryKey: ['market', 'outliers', query ?? ''],
    queryFn: () => hubApi.outlierScan(query as string),
    enabled: query !== null && query.trim().length > 1,
    staleTime: SEARCH_STALE_MS,
  });
}

/**
 * Underpriced listings across the cards you already care about.
 *
 * Cheap: it reads the last sweep rather than running one, so the tab can
 * load it on open. Refetched on an interval so a tab left open catches
 * the next sweep.
 */
export function useStandingOutliers() {
  return useQuery({
    queryKey: ['market', 'outliers', 'standing'],
    queryFn: () => hubApi.standingOutliers(),
    refetchInterval: 5 * 60 * 1000,
  });
}

/** Run the sweep now rather than waiting for the hour. */
export function useSweepOutliers() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => hubApi.sweepOutliers(),
    onSuccess: (found) =>
      client.setQueryData(['market', 'outliers', 'standing'], found),
  });
}

/** Which catalogues exist, and whether each has been built. */
export function useCatalogues() {
  return useQuery({ queryKey: ['catalogues'], queryFn: () => hubApi.catalogues() });
}

/**
 * One catalogue's arbitrage board.
 *
 * Never builds: a read that could take four minutes is not a read. An
 * unbuilt catalogue comes back with `ready: false` so the page can offer
 * to build it instead of appearing broken.
 */
export function useCatalogueBoard(key: string, keywords: string, maxRaw: string) {
  return useQuery({
    queryKey: ['catalogue', 'board', key, keywords, maxRaw],
    queryFn: () => hubApi.catalogueBoard(key, keywords, maxRaw),
    staleTime: SEARCH_STALE_MS,
  });
}

/** Read every set in a catalogue. Minutes, and explicitly asked for. */
export function useBuildCatalogue() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (key: string) => hubApi.buildCatalogue(key),
    onSuccess: (board) => {
      client.setQueryData(['catalogue', 'board', board.catalogue, '', ''], board);
      void client.invalidateQueries({ queryKey: ['catalogues'] });
      void client.invalidateQueries({ queryKey: ['catalogue', 'board'] });
    },
  });
}

/**
 * The collection, from the hub when it answers and from Firestore when it does
 * not.
 *
 * **Firestore is the floor, not the ceiling.** The hub enriches - live quotes,
 * names resolved from the catalogue - and wins whenever it replies. What
 * Firestore provides is that losing the Oracle box loses the analysis and not
 * the sight of what you own, which is the whole reason the data moved there.
 *
 * The fallback is used only once the hub has actually failed, not while it is
 * still trying: swapping sources mid-load would show the figures twice, once
 * from each, and the second would look like a correction of the first.
 */
export function useCollection() {
  const durable = useDurableCollection();
  const fromHub = useQuery({
    queryKey: marketKeys.collection(),
    queryFn: () => hubApi.collection(),
  });

  // Only the fields the page reads, rather than pretending to be a query
  // result: an errored query is typed as having no data at all, so claiming
  // otherwise needs a cast through `unknown` that would hide a real mistake
  // later.
  const usingFallback = fromHub.isError && durable.data !== undefined;
  return {
    data: fromHub.data ?? (usingFallback ? durable.data : undefined),
    // An error the page cannot act on is noise: the banner already explains
    // that analysis is offline, and the data below it is real.
    error: usingFallback ? null : fromHub.error,
    isLoading: fromHub.isLoading && !usingFallback,
    isFetching: fromHub.isFetching,
    refetch: fromHub.refetch,
    /** True when what is shown came from Firestore rather than the hub. */
    fromCache: usingFallback,
  };
}

export function useAddToCollection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (body: AddHolding) => hubApi.addToCollection(body),
    onSuccess: () => client.invalidateQueries({ queryKey: marketKeys.collection() }),
  });
}

export function useEditHolding() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, changes }: { itemId: string; changes: EditHolding }) =>
      hubApi.editHolding(itemId, changes),
    onSuccess: () => client.invalidateQueries({ queryKey: marketKeys.collection() }),
  });
}

export function useRemoveFromCollection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => hubApi.removeFromCollection(itemId),
    onSuccess: () => client.invalidateQueries({ queryKey: marketKeys.collection() }),
  });
}

/** Re-read every holding's price. One catalogue request per distinct card. */
export function useRevalueCollection() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: () => hubApi.revalueCollection(),
    onSuccess: (data) => client.setQueryData(marketKeys.collection(), data),
  });
}

/**
 * Liked cards, priced as they stand now rather than as they were kept.
 *
 * Costs a catalogue page read per like, so it is held for as long as a
 * search is and only runs when the list is actually on screen - `enabled`
 * is what stops the collection page paying for it while showing holdings.
 */
export function useLikedCards(enabled: boolean) {
  return useQuery({
    queryKey: LIKED_CARDS_KEY,
    queryFn: () => hubApi.likedCards(),
    enabled,
    staleTime: SEARCH_STALE_MS,
  });
}

/**
 * Watched cards, priced as they stand now rather than as they were kept.
 *
 * Same cost and the same reasoning as the liked list: a page read per card,
 * forced past the cache, because the entire point of a watch list is to see
 * what has moved since.
 */
export function useWatchedCards() {
  return useQuery({
    queryKey: ['saved', 'watching', 'cards'],
    queryFn: () => hubApi.watchedCards(),
    staleTime: SEARCH_STALE_MS,
  });
}

/**
 * Unlike a card that is being shown in the liked list.
 *
 * Drops the row from the list in place rather than invalidating it. The
 * list costs a catalogue page read per card, so refetching the whole thing
 * to remove one row would charge the operator for every card they kept in
 * order to delete the one they did not.
 */
export function useUnlikeFromBoard() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (productKey: string) => hubApi.unlikeCard(productKey),
    onSuccess: (_result, productKey) => {
      client.setQueryData<LikedBoard>(LIKED_CARDS_KEY, (board) =>
        board
          ? {
              results: board.results.filter((row) => row.key !== productKey),
              unresolved: board.unresolved.filter((row) => row.product_key !== productKey),
            }
          : board,
      );
      // The hearts elsewhere read from this, and it is cheap.
      void client.invalidateQueries({ queryKey: ['saved'] });
    },
  });
}

/**
 * Liked cards and saved searches. One request; the page needs both.
 *
 * Falls back to Firestore for the same reason `useCollection` does - these are
 * things a person typed and should be visible whether or not the processor is
 * running. The watched list is not in the fallback: it is the shared poll
 * target joined against subscriptions, which only the hub can assemble.
 */
export function useSaved() {
  const durable = useDurableSaved();
  const fromHub = useQuery({
    queryKey: ['saved'],
    queryFn: () => hubApi.saved(),
  });

  const usingFallback = fromHub.isError && durable.data !== undefined;
  return {
    data: fromHub.data ?? (usingFallback ? durable.data : undefined),
    error: usingFallback ? null : fromHub.error,
    isLoading: fromHub.isLoading && !usingFallback,
    isFetching: fromHub.isFetching,
    refetch: fromHub.refetch,
    /** True when what is shown came from Firestore rather than the hub. */
    fromCache: usingFallback,
  };
}

/**
 * Like or unlike, from one call site.
 *
 * Which way it goes is decided from the current set rather than from a flag
 * passed in, so the heart cannot get out of step with the list behind it.
 */
export function useToggleLike(liked: Set<string>) {
  const client = useQueryClient();
  return useMutation({
    // The return values differ - unlike gives nothing back, like gives the
    // saved row - and neither is used, so both are discarded to one type.
    mutationFn: async (productKey: string): Promise<void> => {
      if (liked.has(productKey)) {
        await hubApi.unlikeCard(productKey);
        return;
      }
      await hubApi.likeCard(productKey);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['saved'] }),
  });
}

/**
 * Watch or stop watching, from one call site.
 *
 * Same shape as `useToggleLike` and deliberately a separate list: liking is
 * "show me this again", watching is "tell me when this moves". A shelf of
 * forty kept cards stays readable only because the handful actually being
 * followed are somewhere else.
 */
export function useToggleWatch(watched: Set<string>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (productKey: string): Promise<void> => {
      if (watched.has(productKey)) {
        await hubApi.unwatchCard(productKey);
        return;
      }
      await hubApi.watchCard(productKey);
    },
    onSuccess: () => client.invalidateQueries({ queryKey: ['saved'] }),
  });
}

/**
 * Like a card straight off the discovery board.
 *
 * The board row carries no product key, so the hub resolves the match. A
 * card the matcher cannot place confidently comes back a 404, which is the
 * matcher refusing to attach another card's prices to this one.
 */
export function useLikeDiscovered() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (discoveryKey: string) => hubApi.likeDiscovered(discoveryKey),
    onSuccess: () => client.invalidateQueries({ queryKey: ['saved'] }),
  });
}

export function useSaveSearch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (params: SaveSearchBody) => hubApi.saveSearch(params),
    onSuccess: () => client.invalidateQueries({ queryKey: ['saved'] }),
  });
}

/** Stamp a saved search as run, so its "last run" column means something. */
export function useMarkSearchRun() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (searchId: string) => hubApi.markSearchRun(searchId),
    onSuccess: () => client.invalidateQueries({ queryKey: ['saved'] }),
  });
}

export function useForgetSearch() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (searchId: string) => hubApi.forgetSearch(searchId),
    onSuccess: () => client.invalidateQueries({ queryKey: ['saved'] }),
  });
}
